import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { FaqItem } from "@/pages/help/faqData";
import {
  diffContentFields,
  draftToPayload,
  hasDraft,
  payloadToContent,
  type FaqContentOverride,
  type FaqContentPayload,
  type FaqVersionContent,
  type FaqVersionRow,
} from "@/pages/help/faqContent";

interface UseFaqContentResult {
  overrides: Record<string, FaqContentOverride>;
  loading: boolean;
  savingKey: string | null;
  saveDraft: (item: FaqItem, payload: FaqContentPayload, editor: string) => Promise<void>;
  publishContent: (item: FaqItem, payload: FaqContentPayload, editor: string) => Promise<void>;
  publishAllDrafts: (items: FaqItem[], editor: string) => Promise<number>;
  resetContent: (item: FaqItem, editor: string) => Promise<void>;
  fetchVersions: (qKey: string) => Promise<FaqVersionRow[]>;
  rollbackTo: (item: FaqItem, version: FaqVersionRow, editor: string) => Promise<void>;
  reload: () => void;
}

function emptyOverride(qKey: string, category: string | null, now: string): FaqContentOverride {
  return {
    q_key: qKey,
    category,
    question_zh: null,
    answer_zh: null,
    question_en: null,
    answer_en: null,
    status: "published",
    draft_question_zh: null,
    draft_answer_zh: null,
    draft_question_en: null,
    draft_answer_en: null,
    version: 0,
    published_at: null,
    updated_at: now,
    updated_by: null,
  };
}

/**
 * 管理端维护 FAQ 内容：
 * - 存草稿（不对外生效）/ 发布（对访客立即生效）/ 重置为默认
 * - 每次保存写入一条版本记录，支持查看版本历史与一键回滚
 * 采用乐观更新，写入失败时自动回滚界面状态。
 */
export function useFaqContent(): UseFaqContentResult {
  const [overrides, setOverrides] = useState<Record<string, FaqContentOverride>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const overridesRef = useRef(overrides);

  useEffect(() => {
    overridesRef.current = overrides;
  }, [overrides]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("faq_content").select("*").limit(1000);
      if (error) throw error;
      const map: Record<string, FaqContentOverride> = {};
      (data ?? []).forEach((row) => {
        const record = row as FaqContentOverride;
        map[record.q_key] = record;
      });
      setOverrides(map);
    } catch {
      /* backend unavailable, keep empty overrides */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const persist = useCallback(async (record: FaqContentOverride) => {
    setSavingKey(record.q_key);
    const previous = overridesRef.current[record.q_key];
    setOverrides((prev) => ({ ...prev, [record.q_key]: record }));
    try {
      const { error } = await supabase.from("faq_content").upsert(record, { onConflict: "q_key" });
      if (error) throw error;
    } catch {
      setOverrides((prev) => {
        const next = { ...prev };
        if (previous) next[record.q_key] = previous;
        else delete next[record.q_key];
        return next;
      });
    } finally {
      setSavingKey(null);
    }
  }, []);

  const recordVersion = useCallback(
    async (qKey: string, content: FaqVersionContent, action: string, editor: string) => {
      try {
        const { data: last } = await supabase
          .from("faq_content_versions")
          .select("question_zh, answer_zh, question_en, answer_en")
          .eq("q_key", qKey)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        const changed = diffContentFields(last ?? undefined, content);
        await supabase.from("faq_content_versions").insert({
          q_key: qKey,
          ...content,
          action,
          changed_fields: changed,
          editor: editor || null,
        });
      } catch {
        /* 版本记录失败不影响内容生效 */
      }
    },
    [],
  );

  const buildRow = useCallback(
    (item: FaqItem, patch: Partial<FaqContentOverride>): FaqContentOverride => {
      const now = new Date().toISOString();
      const base = overridesRef.current[item.qKey] ?? emptyOverride(item.qKey, item.cat, now);
      return { ...base, category: item.cat, ...patch, updated_at: now };
    },
    [],
  );

  const saveDraft = useCallback(
    async (item: FaqItem, payload: FaqContentPayload, editor: string) => {
      const content = payloadToContent(payload);
      const row = buildRow(item, {
        status: "draft",
        draft_question_zh: content.question_zh,
        draft_answer_zh: content.answer_zh,
        draft_question_en: content.question_en,
        draft_answer_en: content.answer_en,
        updated_by: editor || null,
      });
      await persist(row);
      await recordVersion(item.qKey, content, "draft", editor);
    },
    [buildRow, persist, recordVersion],
  );

  const publishContent = useCallback(
    async (item: FaqItem, payload: FaqContentPayload, editor: string) => {
      const content = payloadToContent(payload);
      const prev = overridesRef.current[item.qKey];
      const row = buildRow(item, {
        ...content,
        status: "published",
        draft_question_zh: null,
        draft_answer_zh: null,
        draft_question_en: null,
        draft_answer_en: null,
        version: (prev?.version ?? 0) + 1,
        updated_by: editor || null,
        published_at: new Date().toISOString(),
      });
      await persist(row);
      await recordVersion(item.qKey, content, "publish", editor);
    },
    [buildRow, persist, recordVersion],
  );

  const resetContent = useCallback(
    async (item: FaqItem, editor: string) => {
      const prev = overridesRef.current[item.qKey];
      const row = buildRow(item, {
        question_zh: null,
        answer_zh: null,
        question_en: null,
        answer_en: null,
        status: "published",
        draft_question_zh: null,
        draft_answer_zh: null,
        draft_question_en: null,
        draft_answer_en: null,
        version: (prev?.version ?? 0) + 1,
        updated_by: editor || null,
      });
      await persist(row);
      await recordVersion(
        item.qKey,
        { question_zh: null, answer_zh: null, question_en: null, answer_en: null },
        "reset",
        editor,
      );
    },
    [buildRow, persist, recordVersion],
  );

  const rollbackTo = useCallback(
    async (item: FaqItem, version: FaqVersionRow, editor: string) => {
      const content: FaqVersionContent = {
        question_zh: version.question_zh,
        answer_zh: version.answer_zh,
        question_en: version.question_en,
        answer_en: version.answer_en,
      };
      const prev = overridesRef.current[item.qKey];
      const row = buildRow(item, {
        ...content,
        status: "published",
        draft_question_zh: null,
        draft_answer_zh: null,
        draft_question_en: null,
        draft_answer_en: null,
        version: (prev?.version ?? 0) + 1,
        updated_by: editor || null,
        published_at: new Date().toISOString(),
      });
      await persist(row);
      await recordVersion(item.qKey, content, "rollback", editor);
    },
    [buildRow, persist, recordVersion],
  );

  const publishAllDrafts = useCallback(
    async (items: FaqItem[], editor: string): Promise<number> => {
      let published = 0;
      for (const item of items) {
        const override = overridesRef.current[item.qKey];
        if (!hasDraft(override)) continue;
        await publishContent(item, draftToPayload(override), editor);
        published += 1;
      }
      return published;
    },
    [publishContent],
  );

  const fetchVersions = useCallback(async (qKey: string): Promise<FaqVersionRow[]> => {
    try {
      const { data, error } = await supabase
        .from("faq_content_versions")
        .select("id, q_key, question_zh, answer_zh, question_en, answer_en, action, changed_fields, editor, created_at")
        .eq("q_key", qKey)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as FaqVersionRow[];
    } catch {
      return [];
    }
  }, []);

  return {
    overrides,
    loading,
    savingKey,
    saveDraft,
    publishContent,
    publishAllDrafts,
    resetContent,
    fetchVersions,
    rollbackTo,
    reload: load,
  };
}